import { BaseInteractiveElement } from './component-base.js';

// <toggle-card>: a card with a detail panel that slides up over it (data-open).
// - Click/tap anywhere toggles it, unless data-activator="icon" limits that to
//   [data-tc-activator] elements.
// - On touch screens the open panel ([data-tc-panel]) follows a downward drag and
//   closes once it has been pulled past DRAG_CLOSE_PX; a shorter drag snaps back.
//   Horizontal moves are left alone so a parent slider keeps its swipe.
const DRAG_START_PX = 8;
const DRAG_CLOSE_PX = 60;

class ToggleCardElement extends BaseInteractiveElement {
  constructor() {
    super({ baseClass: '', eventGroup: undefined });
    this._onClick = this._onClick.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._drag = null;
  }
  connectedCallback() {
    try {
      super.connectedCallback();
      this.addEventListener('click', this._onClick, true);
      this.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._onClick(e); } }, true);
      this.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.addEventListener('touchmove', this._onTouchMove, { passive: false });
      this.addEventListener('touchend', this._onTouchEnd);
      this.addEventListener('touchcancel', this._onTouchEnd);
      this._applyState(this.hasAttribute('data-open'));
    } catch (_) {}
  }
  disconnectedCallback() {
    try { this.removeEventListener('click', this._onClick, true); } catch (_) {}
    try {
      this.removeEventListener('touchstart', this._onTouchStart);
      this.removeEventListener('touchmove', this._onTouchMove);
      this.removeEventListener('touchend', this._onTouchEnd);
      this.removeEventListener('touchcancel', this._onTouchEnd);
    } catch (_) {}
    try { super.disconnectedCallback(); } catch (_) {}
  }
  _panel() { return this.querySelector('[data-tc-panel]'); }
  _onTouchStart(ev) {
    if (!this.hasAttribute('data-open') || !this._panel() || ev.touches.length !== 1) return;
    const t = ev.touches[0];
    this._drag = { x: t.clientX, y: t.clientY, dy: 0, active: false };
  }
  _onTouchMove(ev) {
    const d = this._drag;
    if (!d) return;
    const t = ev.touches[0];
    const dx = t.clientX - d.x;
    const dy = t.clientY - d.y;
    if (!d.active) {
      if (Math.abs(dy) < DRAG_START_PX || Math.abs(dy) < Math.abs(dx)) return;
      if (dy < 0) { this._drag = null; return; } // upward: not a close gesture
      d.active = true;
    }
    d.dy = Math.max(0, dy);
    ev.preventDefault(); // the panel takes the gesture, not the page scroll
    const panel = this._panel();
    panel.style.transition = 'none';
    panel.style.translate = 'none';
    panel.style.transform = `translateY(calc(-100% + ${d.dy}px))`;
  }
  _onTouchEnd() {
    const d = this._drag;
    this._drag = null;
    if (!d || !d.active) return;
    const panel = this._panel();
    panel.style.transition = '';
    panel.style.translate = '';
    panel.style.transform = '';
    if (d.dy > DRAG_CLOSE_PX) this._applyState(false);
    this._suppressClick = true; // the touchend also fires a click; don't re-toggle
    setTimeout(() => { this._suppressClick = false; }, 400);
  }
  _applyState(open) {
    try {
      this.toggleAttribute('data-open', !!open);
      this.setAttribute('aria-expanded', String(!!open));
      const group = this.closest('.group') || this.parentElement;
      if (group && group.classList && group.toggleAttribute) {
        // no-op for attributes; class-based transitions rely on group-data-[open]
      }
      // Icon swap
      const plus = this.querySelector('[data-tc-icon-plus]');
      const minus = this.querySelector('[data-tc-icon-minus]');
      if (plus && minus) {
        if (open) { plus.classList.add('hidden'); minus.classList.remove('hidden'); }
        else { plus.classList.remove('hidden'); minus.classList.add('hidden'); }
      }
    } catch (_) {}
  }
  _onClick(ev) {
    try {
      if (this._suppressClick) { ev.preventDefault && ev.preventDefault(); ev.stopPropagation && ev.stopPropagation(); return; }
      // ignore clicks on inner links/buttons
      const inner = ev.target && ev.target.closest && ev.target.closest('a,button');
      if (inner && this.contains(inner)) return;
      // If activator is restricted to icon, only toggle when clicking inside marked activator
      const activatorMode = this.getAttribute('data-activator');
      if (activatorMode === 'icon') {
        const act = ev.target && ev.target.closest && ev.target.closest('[data-tc-activator]');
        if (!act || !this.contains(act)) return;
      }
      const isOpen = this.hasAttribute('data-open');
      const willOpen = !isOpen;
      // If single-open behavior is enabled on an ancestor, close siblings first
      if (willOpen) {
        try {
          let scope = this.closest('[data-toggle-card-single],[single-expand]');
          if (!scope) scope = this.closest('.layout-grid');
          if (scope) {
            const openCards = scope.querySelectorAll('toggle-card[data-open]');
            openCards.forEach((el) => {
              if (el === this) return;
              try { typeof el._applyState === 'function' ? el._applyState(false) : el.removeAttribute('data-open'); } catch (_) {}
            });
          }
        } catch (_) {}
      }
      this._applyState(willOpen);
      ev.preventDefault && ev.preventDefault();
      ev.stopPropagation && ev.stopPropagation();
    } catch (_) {}
  }
}

try {
  const TAG = 'toggle-card';
  if (!customElements.get(TAG)) customElements.define(TAG, ToggleCardElement);
} catch (_) {}


